// src/components/DocumentExpiringView.tsx
// Documents approaching expiration, grouped by urgency.

import { useMemo } from 'react';
import { useI18n } from '@react/hooks';
import {
  Badge,
  Button,
  Card,
  CardContent,
  ErrorAlert,
} from '@react/components';

import {
  getEntityId,
  getDocumentType,
  getDocumentOwner,
  getStatusMapping,
  documentTypeMap,
  formatDate,
} from '@/features/document/logic';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DocumentExpiringViewProps {
  documents: any[];
  onRenew: (id: string) => void;
  onSelect: (id: string) => void;
  error?: string | null;
  isLoading?: boolean;
}

// ---------------------------------------------------------------------------
// Urgency grouping
// ---------------------------------------------------------------------------

type UrgencyGroup = 'overdue' | 'this_week' | 'this_month';

interface GroupConfig {
  key: UrgencyGroup;
  labelKey: string;
  indicatorClass: string;
  badgeVariant: string;
}

const URGENCY_GROUPS: GroupConfig[] = [
  { key: 'overdue', labelKey: 'document.expiring.overdue', indicatorClass: 'bg-destructive', badgeVariant: 'error' },
  { key: 'this_week', labelKey: 'document.expiring.thisWeek', indicatorClass: 'bg-warning', badgeVariant: 'warning' },
  { key: 'this_month', labelKey: 'document.expiring.thisMonth', indicatorClass: 'bg-primary', badgeVariant: 'primary' },
];

function getUrgencyGroup(doc: any): UrgencyGroup {
  const dateStr = doc?.expiry_date || doc?.review_date;
  if (!dateStr) return 'this_month';
  const now = Date.now();
  const ts = new Date(dateStr).getTime();
  if (ts < now) return 'overdue';
  const daysUntil = (ts - now) / (1000 * 60 * 60 * 24);
  if (daysUntil <= 7) return 'this_week';
  return 'this_month';
}

function getDaysUntilExpiry(doc: any): number {
  const dateStr = doc?.expiry_date || doc?.review_date;
  if (!dateStr) return 0;
  return Math.floor((new Date(dateStr).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function ExpiringSkeleton() {
  return (
    <div className="space-y-6">
      {Array.from({ length: 3 }).map((_, gi) => (
        <div key={gi} className="space-y-2">
          <div className="h-5 w-32 rounded bg-muted animate-pulse" />
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="rounded-lg border border-border p-4 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="h-3 w-3 rounded-full bg-muted" />
                <div className="h-4 flex-1 rounded bg-muted" />
                <div className="h-8 w-20 rounded bg-muted" />
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DocumentExpiringView({
  documents,
  onRenew,
  onSelect,
  error,
  isLoading,
}: DocumentExpiringViewProps) {
  const { t, locale } = useI18n();

  // Group documents by urgency
  const grouped = useMemo(() => {
    const groups: Record<UrgencyGroup, any[]> = {
      overdue: [],
      this_week: [],
      this_month: [],
    };
    for (const doc of documents) {
      const group = getUrgencyGroup(doc);
      groups[group].push(doc);
    }
    // Sort within each group by expiry date (soonest first)
    for (const key of Object.keys(groups) as UrgencyGroup[]) {
      groups[key].sort((a: any, b: any) => {
        const aDate = new Date(a?.expiry_date || a?.review_date || 0).getTime();
        const bDate = new Date(b?.expiry_date || b?.review_date || 0).getTime();
        return aDate - bDate;
      });
    }
    return groups;
  }, [documents]);

  if (error) {
    return <ErrorAlert error={error} />;
  }

  if (isLoading) {
    return <ExpiringSkeleton />;
  }

  if (documents.length === 0) {
    return (
      <div className="py-16 text-center">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <svg className="h-8 w-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-lg font-medium text-foreground">{t('document.expiring.emptyTitle')}</p>
        <p className="mt-1 text-sm text-muted-foreground">{t('document.expiring.emptyDescription')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {URGENCY_GROUPS.map((groupConfig) => {
        const docs = grouped[groupConfig.key];
        if (docs.length === 0) return null;

        return (
          <div key={groupConfig.key}>
            {/* Group header */}
            <div className="mb-2 flex items-center gap-2">
              <div className={`h-2.5 w-2.5 rounded-full ${groupConfig.indicatorClass}`} />
              <h3 className="text-sm font-semibold text-foreground">
                {t(groupConfig.labelKey)}
              </h3>
              <Badge variant={groupConfig.badgeVariant as any} size="sm">
                {docs.length}
              </Badge>
            </div>

            {/* Items */}
            <div className="space-y-2">
              {docs.map((doc: any) => {
                const id = getEntityId(doc);
                const docType = getDocumentType(doc);
                const owner = getDocumentOwner(doc);
                const typeInfo = getStatusMapping(documentTypeMap, docType);
                const daysUntil = getDaysUntilExpiry(doc);
                const expiryDate = doc?.expiry_date || doc?.review_date;

                return (
                  <Card
                    key={id}
                    className="cursor-pointer transition-colors hover:border-primary/20"
                    onClick={() => onSelect(id)}
                  >
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                          {/* Urgency dot */}
                          <div className={`h-2 w-2 rounded-full flex-shrink-0 ${groupConfig.indicatorClass}`} />

                          {/* Title and meta */}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground truncate">
                              {doc.title || t('common.untitled')}
                            </p>
                            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                              <Badge variant={typeInfo.variant as any} size="sm">
                                {t(typeInfo.labelKey)}
                              </Badge>
                              {expiryDate && (
                                <span>{t('document.expiring.expiresOn')}: {formatDate(expiryDate, locale)}</span>
                              )}
                              {owner && (
                                <span>{t('document.owner')}: {owner}</span>
                              )}
                            </div>
                          </div>

                          {/* Days indicator */}
                          <div className="flex-shrink-0 text-right">
                            <span className={`text-sm font-semibold ${
                              daysUntil < 0 ? 'text-destructive' :
                              daysUntil <= 7 ? 'text-warning' :
                              'text-primary'
                            }`}>
                              {daysUntil < 0
                                ? t('document.expiring.overdueDays', { days: Math.abs(daysUntil).toString() })
                                : t('document.expiring.daysLeft', { days: daysUntil.toString() })
                              }
                            </span>
                          </div>
                        </div>

                        {/* Renew action */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            onRenew(id);
                          }}
                        >
                          {t('document.expiring.renew')}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default DocumentExpiringView;
